<script setup lang="ts">
// ================================
// IMPORTS
// ================================
import { computed, ref } from "vue"
import { storeToRefs } from "pinia"
import { useI18n } from "vue-i18n"
import { useComparatorStore } from "@modules/courtage/stores/comparator.store"
import { formatNumber, formatAddress } from "@core/helpers"
import ComparatorEditCeeModal from "@modules/courtage/components/comparator/edit/ComparatorEditCeeModal.vue"

// ================================
// INTERNATIONALIZATION
// ================================
const { t } = useI18n({
    messages: {
        fr: {
            label: {
                titulaire: "Titulaire",
                naf: "Code APE / NAF du titulaire",
                adresse: "Adresse",
                fta: "FTA",
                puissRaccord: "Puiss. de raccordement",
                puissSouscrite: "Puiss. souscrite",
                denivele: "Dénivelé de puissance",
                calendrier: "Calendrier fournisseur",
                consoAnnuelle: "Consommation annuelle",
                cee: "Conso. éligible CEE",
                hphc: "<strong>HP</strong> {hp} kWh/an <span class='text-sky-400'>•</span> <strong>HC</strong> {hc} kWh/an",
            },
        },
    },
})

// ================================
// TYPES & INTERFACES
// ================================

/**
 * Configuration d'un composant modal d'édition pour les champs modifiables
 */
type EditConfig = {
    component: typeof ComparatorEditCeeModal
    props?: Record<string, any>
    on?: Record<string, (...args: any[]) => void>
    open?: () => void
}

/**
 * Configuration d'un champ d'information du compteur
 */
type Field = {
    key: string
    label: string
    value?: any
    html?: boolean
    show?: boolean
    onClick?: () => void
    edit?: EditConfig | null
    loading?: boolean
    format?: (value?: any) => string
}

// ================================
// PROPS & EMITS
// ================================
// Aucun prop ou emit défini pour ce composant

// ================================
// REACTIVE STATE
// ================================

/**
 * Store du comparateur pour accéder aux données métier
 */
const comparator = useComparatorStore()

/**
 * Références réactives aux valeurs du store
 */
const {
    codeNaf,
    consommationCee,
    getDenivelePuissance,
    isFetchingDataPeriode,
    isFetchingNafCode,
} = storeToRefs(comparator)

/**
 * État d'ouverture de la modal d'édition CEE
 */
const isCeeModalOpen = ref<boolean>(false)

// ================================
// COMPUTED PROPERTIES
// ================================

/**
 * Liste des champs d'information du compteur avec leur configuration
 */
const getFields = computed<Field[]>(() => {
    // Récupération de la source sélectionnée depuis le store
    const source = comparator.getSourceById

    if (!source) return []

    // Détermination du segment du compteur pour la logique conditionnelle
    const segment = source?.informations?.pdl?.compteur?.segment ?? "UNDEFINED"

    // ================================
    // CONFIGURATIONS DES MODALES
    // ================================
    /**
     * Configuration de la modal d'édition CEE
     */
    const ceeModalConfig: EditConfig = {
        component: ComparatorEditCeeModal,
        props: {
            open: isCeeModalOpen.value,
            value: consommationCee.value,
        },
        on: {
            "update:open": (value: boolean) => (isCeeModalOpen.value = value),
            submit: (value: number) => onCeeSubmit(value),
        },
        open: () => (isCeeModalOpen.value = true),
    }

    // ================================
    // DÉFINITION DES CHAMPS
    // ================================

    const fields: Field[] = [
        {
            key: "titulaire",
            label: t("label.titulaire"),
            value: getTitulaireLabel(),
        },
        {
            key: "naf",
            label: t("label.naf"),
            value: codeNaf.value || "—",
            loading: isFetchingNafCode.value,
            show: shouldShowNafField(),
        },
        {
            key: "adresse",
            label: t("label.adresse"),
            value: formatAddress(source.address) || "—",
        },
        {
            key: "fta",
            label: t("label.fta"),
            value: getFtaValue(),
        },
        {
            key: "puissRaccord",
            label: t("label.puissRaccord"),
            value: formatNumber(getPuissanceRaccordement(), {
                unit: "kVA",
                defaultValue: "—",
            }),
        },
        {
            key: "puissSouscrite",
            label: t("label.puissSouscrite"),
            value: formatNumber(getPuissanceSouscrite(), {
                unit: "kVA",
                defaultValue: "—",
            }),
            show: ["C5", "P4"].includes(segment),
        },
        {
            key: "denivele",
            label: t("label.denivele"),
            value: getDenivelePuissance.value,
            show: !["C5", "P4"].includes(segment),
        },
        {
            key: "calendrier",
            label: t("label.calendrier"),
            show: ["C5", "P4"].includes(segment),
            value: getCalendrierValue(),
        },
        {
            key: "cee",
            label: t("label.cee"),
            loading: isFetchingDataPeriode.value,
            value: formatNumber(consommationCee.value, {
                unit: "kWh/an",
                defaultValue: "—",
            }),
            edit: ceeModalConfig,
        },
    ]

    return fields.filter((field) => field.show !== false)
})

// ================================
// METHODS
// ================================

/**
 * Gestion de la soumission de la modal CEE
 * Met à jour la consommation CEE dans le store
 * @param value - Nouvelle valeur de consommation CEE
 */
function onCeeSubmit(value: number): void {
    consommationCee.value = value
}

/**
 * Formate le label du titulaire selon le type (société ou utilisateur)
 * @returns Le nom du titulaire ou "—" si non défini
 */
function getTitulaireLabel(): string {
    const titulaire =
        comparator.getSourceById?.informations?.pdl?.consentement?.titulaire
    if (!titulaire) return "—"

    const company = titulaire.company
    const user = titulaire.user

    if (company?.name) return company.name
    if (user?.firstname || user?.lastname) {
        return [user?.firstname, user?.lastname].filter(Boolean).join(" ")
    }
    return "—"
}

/**
 * Détermine si le champ NAF doit être affiché
 * @returns true si le champ doit être visible
 */
function shouldShowNafField(): boolean {
    const source = comparator.getSourceById
    return !!(
        source?.informations?.pdl?.consentement?.titulaire?.company
            ?.idcompany &&
        source?.informations?.pdl?.consentement?.titulaire?.company?.idtype ===
            "siren"
    )
}

/**
 * Récupère la valeur FTA depuis la source
 * @returns La valeur FTA ou "—" si non définie
 */
function getFtaValue(): string {
    const source = comparator.getSourceById
    return (
        source?.informations?.pdl?.structure_tarifaire
            ?.formule_tarifaire_acheminement?.fta ?? "—"
    )
}

/**
 * Récupère la puissance de raccordement
 * @returns La puissance de raccordement ou NaN
 */
function getPuissanceRaccordement(): number {
    const source = comparator.getSourceById
    return (
        source?.informations?.pdl?.compteur?.raccordement
            ?.puissance_raccordement_soutirage ?? NaN
    )
}

/**
 * Récupère la puissance souscrite
 * @returns La puissance souscrite ou undefined
 */
function getPuissanceSouscrite(): number | undefined {
    const source = comparator.getSourceById
    const puissance =
        source?.informations?.pdl?.structure_tarifaire
            ?.puissance_souscrite_soutirage?.puissance
    return puissance ?? undefined
}

/**
 * Récupère le code du calendrier fournisseur
 * @returns Le code calendrier ou "—" si non défini
 */
function getCalendrierValue(): string {
    const source = comparator.getSourceById
    return (
        source?.informations?.pdl?.structure_tarifaire?.calendrier_fournisseur
            ?.code ?? "—"
    )
}
</script>

<template>
    <!-- ================================ -->
    <!-- COMPOSANT PRINCIPAL -->
    <!-- ================================ -->
    <div
        class="comparator-meter-info-data grid grid-cols-1 gap-x-8 text-base leading-6 text-[color:var(--p-surface-800)]"
    >
        <!-- ================================ -->
        <!-- LISTE DES CHAMPS D'INFORMATION -->
        <!-- ================================ -->
        <BaseLineBlock
            v-for="field in getFields"
            :key="field.key"
            :line="{
                text: field.label,
                value: field.html ? '' : field.value,
                id: field.key,
            }"
            :loading="field.loading"
            :edit="!!field.edit"
            @click:edit="field.edit && field.edit.open && field.edit.open()"
            class="flex flex-col gap-4 md:gap-0"
        >
            <!-- ================================ -->
            <!-- AFFICHAGE DES VALEURS -->
            <!-- ================================ -->
            <template v-if="field.html">
                <div v-html="field.value" />
            </template>

            <template #denivele="{ value }">
                <MeterPdlDeniveleViewer :denivele="value" />
            </template>

            <!-- ================================ -->
            <!-- BOUTONS D'ÉDITION -->
            <!-- ================================ -->
            <template #actions v-if="field.edit">
                <component
                    :is="field.edit.component"
                    v-bind="field.edit.props"
                    v-on="field.edit.on"
                />
            </template>
        </BaseLineBlock>
    </div>
</template>
